import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";

interface Test {
  id: string;
  name: string;
  description?: string;
  group?: string;
  isAutomatable: boolean;
  createdAt: string;
  credentials?: {
    apiKey?: string;
  };
}

interface FileUpload {
  id: string;
  testId: string;
  name: string;
  state: "READY" | "ERROR" | "PROCESSING";
  extension: string;
  createdAt: string;
}

export function TestFilesPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  
  const acceptedFileTypes = ".csv,.pdf,.xlsx,.xls";
  const allowedTypes = ["csv", "pdf", "xlsx", "xls"];

  // Load test data and files
  useEffect(() => {
    if (testId) {
      loadTest();
      loadFiles();
    }
  }, [testId]);

  const loadTest = async () => {
    if (!testId) return;
    
    try {
      setIsLoading(true);
      const testData = await api.get<Test>(`/test/${testId}`);
      setTest(testData);
    } catch (error) {
      console.error("Failed to load test:", error);
      showError("Failed to load test", "Could not load test details. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  };

  const loadFiles = async () => {
    if (!testId) return;
    
    try {
      setIsLoadingFiles(true);
      const response = await api.get<FileUpload[]>(`/test/${testId}/files`);
      setFiles(response);
    } catch (error) {
      console.error("Failed to load files:", error);
      showError("Failed to load files", "Could not load file list. Please try again.");
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file || !testId) return;
    
    // Validate file type
    const fileType = file.name.split('.').pop()?.toLowerCase();
    if (!fileType || !allowedTypes.includes(fileType)) {
      showError("Invalid file type", "Please upload a CSV, PDF, XLSX, or XLS file.");
      return;
    }
    
    try {
      setIsUploadingFile(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`/api/test/${testId}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      const uploadedFile = await response.json();
      setFiles(prev => [...prev, uploadedFile]);
      showSuccess("File uploaded", "File uploaded successfully!");
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      showError("Upload failed", "Failed to upload file. Please try again.");
    } finally {
      setIsUploadingFile(false);
    }
  };
  
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleDeleteClick = (file: FileUpload) => {
    setFileToDelete({ id: file.id, name: file.name });
    setShowDeleteModal(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!testId || !fileToDelete) return;
    
    try {
      setIsDeletingFile(true);
      
      // The api.delete method already handles response parsing and error checking
      await api.delete(`/test/${testId}/files/${fileToDelete.id}`);
      
      setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      showSuccess("File deleted", "File deleted successfully!");
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (error) {
      console.error('Failed to delete file:', error);
      showError("Delete failed", "Failed to delete file. Please try again.");
    } finally {
      setIsDeletingFile(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };
  
  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = () => {
    setDragOver(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const headerActions = (
    <Button
      onClick={handleUploadClick}
      size="sm"
      className="text-xs"
      disabled={isUploadingFile}
    >
      {isUploadingFile ? "Uploading..." : "Upload Files"}
    </Button>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="files"
        onToggleChange={handleToggleChange}
        actions={headerActions}
      />

      <div className="flex-1 overflow-auto p-6">
        <div className=" mx-auto">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Files</h3>
              <p className="text-sm text-muted-foreground">
                Upload files to be used in your test workflow
              </p>
            </div>
            
            {/* File upload area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                dragOver 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50'
              } ${
                isUploadingFile ? 'opacity-60 pointer-events-none' : ''
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes}
                onChange={handleFileInputChange}
                className="hidden"
              />
              
              {isUploadingFile ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
                  <p className="text-sm text-muted-foreground">Uploading file...</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Drop your file here or
                  </p>
                  <Button
                    onClick={handleUploadClick}
                    variant="secondary"
                    className="mb-4"
                  >
                    Browse
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Accepted file types: CSV, PDF, XLSX, XLS
                  </p>
                </div>
              )}
            </div>
            
            {/* Files list */}
            {isLoadingFiles ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                <span className="ml-2 text-sm text-muted-foreground">Loading files...</span>
              </div>
            ) : files.length > 0 ? (
              <div className="mt-6">
                <h4 className="text-sm font-medium mb-4">Uploaded Files</h4>
                <div className="space-y-2">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {file.state === 'READY' && (
                            <span className="text-green-500 text-lg">✓</span>
                          )}
                          {file.state === 'ERROR' && (
                            <span className="text-red-500 text-lg">✕</span>
                          )}
                          {file.state === 'PROCESSING' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="text-sm font-medium">{file.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Uploaded {formatDate(file.createdAt)}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(file)}
                        className="text-muted-foreground hover:text-red-500 px-2"
                        title="Delete file"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-6 text-center py-8">
                <p className="text-sm text-muted-foreground">
                  No files uploaded yet. Upload your first file above.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeletingFile}
      />
    </div>
  );
}
